import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ticketsService } from '../api/ticketsService';
import './Tickets.css';

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'open' },
  { label: 'Pending', value: 'pending' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Closed', value: 'closed' },
];

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: '',
    company_id: '',
  });

  const role = localStorage.getItem('userRole');
  const userCompanyId = localStorage.getItem('userCompanyId');
  const navigate = useNavigate();

  // Auto-populate company_id for cert admins on form open
  useEffect(() => {
    if (showNew && role === 'third_party_admin' && userCompanyId) {
      setForm((f) => ({ ...f, company_id: userCompanyId }));
    }
  }, [showNew, role, userCompanyId]);

  const loadTickets = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await ticketsService.list(statusFilter || undefined);
      setTickets(res.data || []);
    } catch (err) {
      setError('Failed to load tickets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const createTicket = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // For admins, company_id is required; for clients, it's determined server-side
      if (role !== 'client') {
        if (!form.company_id || !form.company_id.trim()) {
          setError('Company ID is required for admins');
          return;
        }
      }

      const payload = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        category: form.category || null,
      };
      
      if (role !== 'client' && form.company_id) {
        payload.company_id = Number(form.company_id);
      }
      
      const res = await ticketsService.create(payload);
      setShowNew(false);
      setForm({ title: '', description: '', priority: 'normal', category: '', company_id: '' });
      setTickets([res.data, ...tickets]);
      navigate(`/tickets/${res.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create ticket');
      console.error(err);
    }
  };

  return (
    <div className="tickets-page">
      <div className="tickets-header">
        <div>
          <h2>Tickets</h2>
          <p className="subtext">Support tickets between clients, cert bodies, and super admins</p>
        </div>
        <div className="actions">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button className="primary" onClick={() => setShowNew((v) => !v)}>
            {showNew ? 'Close' : 'New Ticket'}
          </button>
        </div>
      </div>

      {showNew && (
        <form className="ticket-form" onSubmit={createTicket}>
          <div className="field">
            <label>Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div className="field">
            <label>Description</label>
            <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          </div>
          <div className="field-row">
            <div className="field">
              <label>Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="field">
              <label>Category</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Optional" />
            </div>
            {role !== 'client' && (
              <div className="field">
                <label>Company ID</label>
                <input value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} placeholder="Required for admins" />
              </div>
            )}
          </div>
          <div className="actions" style={{ justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setShowNew(false)}>Cancel</button>
            <button type="submit" className="primary">Create Ticket</button>
          </div>
        </form>
      )}

      {error && <div className="error">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="ticket-table">
          <div className="ticket-table-header">
            <div>ID</div>
            <div>Title</div>
            <div>Status</div>
            <div>Priority</div>
            <div>Updated</div>
          </div>
          {tickets.map((t) => (
            <Link to={`/tickets/${t.id}`} key={t.id} className="ticket-row">
              <div>#{t.id}</div>
              <div>{t.title}</div>
              <div className={`pill ${t.status}`}>{t.status}</div>
              <div className={`pill priority-${t.priority}`}>{t.priority}</div>
              <div>{new Date(t.updated_at).toLocaleString()}</div>
            </Link>
          ))}
          {tickets.length === 0 && <div className="empty">No tickets found.</div>}
        </div>
      )}
    </div>
  );
}
